<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :open-on-click="false"
    :open-on-focus="false"
    transition="scale-transition"
    offset-y
    min-width="auto"
    :disabled="disabled || !showPicker"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        ref="input"
        v-model="displayValue"
        v-bind="attrs"
        v-on="on"
        :label="translatedLabel"
        :name="name || label"
        :hint="hintText"
        :placeholder="placeholderText"
        :append-icon="appendIcon"
        inputmode="numeric"
        :maxlength="utilizarSegundo ? 8 : 5"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        @input.native="onNativeInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.alt.down.prevent="openPicker"
        @keydown.esc="menu = false"
        @click:append="openPicker"
        @click:clear="onClear"
        autocomplete="off"
        :rules="validationRules"
        :error-messages="errorMessages"
        :clearable="clearable"
        :success="showSuccess"
        :error="showError"
        :color="isFocused ? 'primary' : undefined"
        :aria-label="translatedLabel"
        :aria-invalid="showError ? 'true' : 'false'"
        :aria-required="obrigatorio ? 'true' : 'false'"
        validate-on-blur
        hide-details="auto"
        class="
          input-style
          font-size-input
          text-light-input
          placeholder-light
          input-icon
        "
        dense
        flat
        filled
        solo
        :disabled="disabled"
      >
      </v-text-field>
    </template>
    <v-time-picker
      v-model="pickerTime"
      format="24hr"
      :use-seconds="utilizarSegundo"
      @input="onPickerTime"
      @mousedown.capture="onMenuPointerDown"
    />
  </v-menu>
</template>

<script>
import { translateIfMessageExists } from "@/utils/i18n";

export default {
  name: "CampoHorario",

  props: {
    label: { type: String, default: "Data" },
    name: { type: String, required: false },
    payload: { type: String, required: false },
    utilizarSegundo: { type: Boolean, default: false }, // NOVO PROP
    obrigatorio: { type: Boolean, default: true },
    clearable: { type: Boolean, default: true },
    showPicker: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },

  mounted() {
    setTimeout(() => {
      this.setFromPayload(this.payload);
    }, 200);
  },

  watch: {
    payload(val) {
      this.setFromPayload(val);
    },
    utilizarSegundo() {
      this.displayValue = this.formatDisplayValue(this.internalValue);
    },
    menu(val) {
      if (val) {
        this.syncPickerFromValue();
      }
    },
  },

  data() {
    return {
      internalValue: "",
      displayValue: "",
      menu: false,
      pickerTime: null,
      isFocused: false,
      ignoreBlur: false,
    };
  },

  computed: {
    maskPattern() {
      return this.utilizarSegundo ? "##:##:##" : "##:##";
    },
    maxDigits() {
      return this.utilizarSegundo ? 6 : 4;
    },

    hintText() {
      return this.utilizarSegundo ? "HH:MM:SS" : "HH:MM";
    },

    placeholderText() {
      if (!this.isFocused && this.displayValue) {
        return "";
      }
      return this.hintText;
    },

    appendIcon() {
      return this.showPicker ? "mdi-clock-outline" : undefined;
    },

    translatedLabel() {
      return translateIfMessageExists(this.$i18n, this.label);
    },

    validationRules() {
      if (!this.obrigatorio) return [];
      return [(value) => !!value || this.$t("Campo_obrigatorio")];
    },

    isCompleteTime() {
      return this.utilizarSegundo
        ? this.displayValue.length === 8
        : this.displayValue.length === 5;
    },

    formatedValue() {
      const display = this.normalizeSecondsOnBlur(this.displayValue);
      if (!display) {
        return null;
      }
      if (!this.isValidTime(display)) {
        return null;
      }
      return this.normalizeInternal(display);
    },

    showSuccess() {
      return this.isCompleteTime && this.isValidTime(this.displayValue);
    },

    showError() {
      return this.isCompleteTime && !this.isValidTime(this.displayValue);
    },

    errorMessages() {
      if (!this.displayValue || !this.isCompleteTime) {
        return [];
      }
      return this.isValidTime(this.displayValue)
        ? []
        : this.$t("Horario_invalido");
    },
  },

  methods: {
    onFocus() {
      this.isFocused = true;
    },

    setFromPayload(dateTime) {
      const formatted = this.formatTime(dateTime);
      this.internalValue = formatted;
      this.displayValue = this.formatDisplayValue(formatted);
    },

    formatDisplayValue(value) {
      const digits = this.onlyDigits(value).slice(0, this.maxDigits);
      const sanitized = this.sanitizeTimeDigits(digits);
      return this.formatWithPattern(sanitized, this.maskPattern);
    },

    formatTime(dateTime) {
      if (!dateTime) {
        return "";
      }

      let time;

      if (dateTime.length === 19 && dateTime.includes("T")) {
        // Formato ISO 8601
        time = dateTime.substring(11, 19); // HH:MM:SS
      } else if (dateTime.includes(":")) {
        time = dateTime; // Pode ser HH:MM ou HH:MM:SS
      } else {
        return "";
      }

      // Ajusta se estiver só HH:MM e for necessário segundos
      if (this.utilizarSegundo && time.length === 5) {
        time += ":00";
      }

      // Remove segundos se não precisar
      if (!this.utilizarSegundo && time.length === 8) {
        time = time.substring(0, 5);
        time += ":00"; // Mantém internalValue padronizado
      }

      return time;
    },

    openPicker() {
      if (this.showPicker) {
        this.menu = true;
      }
    },

    onPickerTime(value) {
      if (!value) {
        return;
      }
      this.pickerTime = value;
      const normalized = this.utilizarSegundo ? value : value.substring(0, 5);
      this.displayValue = normalized;
      this.internalValue = this.normalizeInternal(normalized);
      this.menu = false;
      this.$emit("date", this.formatedValue);
    },

    syncPickerFromValue() {
      if (!this.internalValue) {
        this.pickerTime = null;
        return;
      }
      if (this.utilizarSegundo) {
        this.pickerTime = this.internalValue.substring(0, 8);
      } else {
        this.pickerTime = this.internalValue.substring(0, 5);
      }
    },

    onNativeInput(event) {
      const raw = (event && event.target && event.target.value) || "";
      const selectionStart =
        event && event.target ? event.target.selectionStart : null;
      const timeMatch = String(raw || "").match(
        /^(\d{2}):(\d{2})(?::(\d{2}))?$/
      );
      if (timeMatch) {
        const second = this.utilizarSegundo ? timeMatch[3] || "" : "";
        const digits = `${timeMatch[1]}${timeMatch[2]}${second}`;
        const sanitized = this.sanitizeTimeDigits(digits);
        const formatted = this.formatWithPattern(sanitized, this.maskPattern);
        this.displayValue = formatted;
        this.internalValue = this.normalizeInternal(formatted);
        this.setCaret(formatted.length);
        return;
      }
      const trimmed = String(raw || "").trim();
      if (/[a-zA-Z]/.test(trimmed)) {
        this.displayValue = raw;
        return;
      }
      const { formatted, caret } = this.applyMask(raw, selectionStart);
      this.displayValue = formatted;
      this.internalValue = this.normalizeInternal(formatted);
      if (caret !== null) {
        this.setCaret(caret);
      }
    },

    applyMask(raw, selectionStart) {
      const digits = this.onlyDigits(raw).slice(0, this.maxDigits);
      const sanitized = this.sanitizeTimeDigits(digits);
      const formatted = this.formatWithPattern(sanitized, this.maskPattern);
      if (typeof selectionStart !== "number") {
        return { formatted, caret: null };
      }
      const digitsBeforeCaret = this.onlyDigits(
        raw.slice(0, selectionStart)
      ).length;
      const preferSeparator =
        selectionStart > 0 &&
        raw[selectionStart - 1] &&
        !/\d/.test(raw[selectionStart - 1]);
      const caret = this.caretFromDigits(
        formatted,
        digitsBeforeCaret,
        preferSeparator
      );
      return { formatted, caret };
    },

    sanitizeTimeDigits(digits) {
      if (!digits) {
        return "";
      }
      const hour = digits.slice(0, 2);
      const minute = digits.slice(2, 4);
      const second = this.utilizarSegundo ? digits.slice(4, 6) : "";
      const safeHour = this.clampSegment(hour, 23, 0);
      const safeMinute = this.clampSegment(minute, 59, 0);
      const safeSecond = this.utilizarSegundo
        ? this.clampSegment(second, 59, 0)
        : "";
      return `${safeHour}${safeMinute}${safeSecond}`;
    },

    clampSegment(segment, max, min = 0) {
      if (!segment || segment.length < 2) {
        return segment || "";
      }
      let num = Number(segment);
      if (Number.isNaN(num)) {
        return segment;
      }
      if (num < min) {
        num = min;
      }
      if (num > max) {
        num = max;
      }
      return String(num).padStart(2, "0");
    },

    normalizeInternal(display) {
      if (!display) {
        return "";
      }
      if (this.utilizarSegundo) {
        return display;
      }
      if (display.length >= 5) {
        return display.substring(0, 5) + ":00";
      }
      return display;
    },

    normalizeKeyword(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },

    applyShortcut() {
      const rawValue = this.displayValue || "";
      if (!rawValue) {
        return false;
      }
      const normalized = this.normalizeKeyword(rawValue);
      if (normalized !== "agora") {
        return false;
      }
      const now = new Date();
      const hour = this.padZero(now.getHours());
      const minute = this.padZero(now.getMinutes());
      const second = this.padZero(now.getSeconds());
      const display = this.utilizarSegundo
        ? `${hour}:${minute}:${second}`
        : `${hour}:${minute}`;
      this.displayValue = display;
      this.internalValue = this.normalizeInternal(display);
      return true;
    },

    isValidTime(display) {
      const digits = this.onlyDigits(display);
      if (this.utilizarSegundo && digits.length < 6) {
        return false;
      }
      if (!this.utilizarSegundo && digits.length < 4) {
        return false;
      }
      const parts = display.split(":");
      const hour = Number(parts[0]);
      const minute = Number(parts[1]);
      const second = this.utilizarSegundo ? Number(parts[2]) : 0;
      if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return false;
      }
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return false;
      }
      if (this.utilizarSegundo && (second < 0 || second > 59)) {
        return false;
      }
      return true;
    },

    padZero(value) {
      return String(value).padStart(2, "0");
    },

    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },

    formatWithPattern(digits, pattern) {
      if (!digits) {
        return "";
      }
      let result = "";
      let digitIndex = 0;
      for (let i = 0; i < pattern.length; i++) {
        const char = pattern[i];
        if (char === "#") {
          if (digitIndex < digits.length) {
            result += digits[digitIndex];
            digitIndex += 1;
          } else {
            break;
          }
        } else if (digitIndex < digits.length) {
          result += char;
        }
      }
      return result;
    },

    caretFromDigits(formatted, digitsCount, preferSeparator) {
      if (!formatted || digitsCount <= 0) {
        return 0;
      }
      let count = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          count += 1;
          if (count === digitsCount) {
            if (
              preferSeparator &&
              formatted[i + 1] &&
              /\D/.test(formatted[i + 1])
            ) {
              return i + 2;
            }
            return i + 1;
          }
        }
      }
      return formatted.length;
    },

    getInputEl() {
      if (!this.$refs.input || !this.$refs.input.$el) {
        return null;
      }
      return this.$refs.input.$el.querySelector("input");
    },

    setCaret(position) {
      this.$nextTick(() => {
        const input = this.getInputEl();
        if (input && typeof input.setSelectionRange === "function") {
          input.setSelectionRange(position, position);
        }
      });
    },

    onBlur() {
      this.isFocused = false;
      if (this.ignoreBlur) {
        this.ignoreBlur = false;
        return;
      }
      const applied = this.applyShortcut();
      if (!applied && /[a-zA-Z]/.test(this.displayValue || "")) {
        this.displayValue = "";
        this.internalValue = "";
      }
      this.displayValue = this.normalizeSecondsOnBlur(this.displayValue);
      this.internalValue = this.formatedValue || "";
      this.menu = false;
      this.$emit("date", this.formatedValue);
    },

    onClear() {
      this.displayValue = "";
      this.internalValue = "";
      this.pickerTime = null;
      this.menu = false;
      this.$emit("date", null);
    },

    onMenuPointerDown() {
      this.ignoreBlur = true;
    },

    normalizeSecondsOnBlur(display) {
      if (!display || !this.utilizarSegundo) {
        return display;
      }
      if (display.length !== 5) {
        return display;
      }
      const [hourText, minuteText] = display.split(":");
      const hour = Number(hourText);
      const minute = Number(minuteText);
      if (
        Number.isNaN(hour) ||
        Number.isNaN(minute) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59
      ) {
        return display;
      }
      return `${this.padZero(hour)}:${this.padZero(minute)}:00`;
    },
  },
};
</script>

<style scoped>
/* Estilos personalizados */
</style>
